import { useCallback, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { setUser } from 'features/auth/authSlice';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useGetEnrolledCareProgramsQuery } from 'services/careProgramService';
import {
  useGetHealthRemindersQuery,
  useGetMedicationRemindersQuery,
} from 'services/remindersService';
import { useUpdateHealthGoalMutation } from 'services/healthGoalService';
import { useLazyGetUserQuery } from 'services/authService';
import { useAppDispatch } from 'store/store';
import { getMembershipDayNumber } from 'utils';
import { HealthGoal } from '../types';

export const useProfile = () => {
  const dispatch = useAppDispatch();
  const { isEnabled } = useFeatureFlags();
  const isProfileEnabled = isEnabled(FeatureFlagKeys.PROFILE_FEATURE);
  const user = useLoggedInUser();
  const [refreshUser] = useLazyGetUserQuery();

  const { data: enrolledCarePrograms = [], error: careProgramsError } =
    useGetEnrolledCareProgramsQuery(
      isProfileEnabled && user?.id ? { programUserId: user.id } : skipToken,
    );
  const { data: medications = [], error: medicationsError } = useGetMedicationRemindersQuery(
    undefined,
    {
      skip: !isProfileEnabled,
    },
  );
  const { data: healthReminders = [], error: remindersError } = useGetHealthRemindersQuery(
    undefined,
    {
      skip: !isProfileEnabled,
    },
  );
  const [updateHealthGoal, { isLoading: isSavingGoal, error: goalError }] =
    useUpdateHealthGoalMutation();

  const submitHealthGoal = useCallback(
    async (healthGoal: HealthGoal, conditions: string[] = []) => {
      if (!isProfileEnabled || !user?.uuid) return;
      try {
        const response = await updateHealthGoal({
          memberUuid: user.uuid,
          health_goal: healthGoal,
          conditions,
        }).unwrap();
        const { data: userData } = await refreshUser();
        if (userData) dispatch(setUser(userData));
      } catch (err) {
        console.error(err);
      }
    },
    [isProfileEnabled, user?.uuid, updateHealthGoal, refreshUser, dispatch],
  );

  const fullName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
  const dayNumber = user?.enrollment_date ? getMembershipDayNumber(user.enrollment_date) : 1;
  const healthGoal = user?.member_health_goal;

  return {
    fullName,
    dayNumber,
    healthGoal,
    enrolledCarePrograms,
    medications,
    healthReminders,
    submitHealthGoal,
    isSavingGoal,
    goalError,
  };
};
